import common from '../common';
import Scene from './Scene';
import IsoWorld from '../IsoWorld';
import Scenario from '../Scenario';
import Tooltip from '../ui/Tooltip';
import HUD from '../hud';
import Text from '../ui/Text';
import SelectTooltip from '../ui/SelectTooltip';
import ConfirmDialog from '../ui/ConfirmDialog';
import AchievementManager from '../AchievementManager';
import AlertDialog from '../ui/AlertDialog';
import screenfull from 'screenfull';
import i18next from 'i18next';
import WarningDialog from '../ui/WarningDialog';

export default class GameScene extends Scene {
	constructor() {
		super();

		this.signals.develop = new P3.utils.MiniSignal();
		this.signals.intro = new P3.utils.MiniSignal();
		this.signals.report = new P3.utils.MiniSignal();

		common.achievements = new AchievementManager();
		common.achievements.signals.unlock.add(this.onAchievementUnlock, this);

		this._world = null;
		this._scenario = null;
		this._gui = null;
		this._tooltip = null;
		this._selectedTile = null;
		this._hideTooltip = false;

		this._dialog = null;
		this._alertDialog = null;

		this._canUpdate = true;

		this._achievementsButton = null;
		this._disasterButton = null;
		this._selectTooltip = null;
		this._inMenu = false;
		this._keyUpSignal = null;

		this._fade = null;

		this.startNowMessage = false;

		if (PIXI.utils.isMobile.any && screenfull.isEnabled) {
			screenfull.on('change', this.onScreenfullChange.bind(this));
		}
	}

	init() {
		super.init();

		common.score = 0;
		common.lastBuild = { id: 0, cost: 0, devcost: 0, special: '' };
		common.advisor = null;

		this._world = new IsoWorld(44, 44);
		this._world.loadMap(P3.assets.json(`level`));
		this._world.hitArea = new PIXI.Rectangle();
		this._world.signals.select.add(this.onWorldTileSelect, this);
		this._world.signals.camerapan.add(this.onCameraPan, this);
		this.addChild(this._world);
		common.world = this._world;

		this._scenario = new Scenario(this._world);
		this._scenario.loadData(P3.assets.json(`scenario${common.selectedScenario}`));
		this._scenario.disaster.signals.disasterEnd.add(this.onDisasterEnd, this);
		this._scenario.disaster.signals.disasterStart.add(this.onDisasterStart, this);
		this._scenario.signals.objectivescomplete.add(this.onObjectivesComplete, this);
		this._scenario.signals.timerend.add(() => this.onDisasterButtonClick(true), this);
		common.scenario = this._scenario;

		this.initGUI();

		this.interactive = true;
		this.on('pointerup', () => {
			if (PIXI.utils.isMobile.any && screenfull.isEnabled) {
				screenfull.request();
			}
		});
		this._keyUpSignal = P3.input.signals.keyUp.add(this._onKeyUp, this);

		this._tooltip = new Tooltip();
		this._tooltip.visible = false;
		this._gui.addChildAt(this._tooltip, 0);

		this._selectTooltip = new SelectTooltip();
		this._selectTooltip.visible = false;
		this._selectTooltip.signals.defendClick.add(this.onDefendClick, this);
		this._selectTooltip.signals.infoClick.add(this.onInfoClick, this);
		this._selectTooltip.signals.developClick.add(this.onDevelopClick, this);
		this._selectTooltip.signals.repeatClick.add(this.onRepeatClick, this);
		this._selectTooltip.signals.demolishClick.add(this.onDemolishClick, this);
		this._selectTooltip.signals.upgradeClick.add(this.onUpgradeClick, this);
		this._world.gui.addChildAt(this._selectTooltip, 0);

		this.signals.intro.dispatch(this);

		if (common.scenario.disaster.ambientsound) {
			common.scenario.disaster.ambientsound.play();
		}
	}

	resize() {
		super.resize();

		this._world.resize();
		this._gui.resize();
		this._scenario.resize();

		this._world.hitArea.x = (common.STAGE_WIDTH - P3.View.width) * 0.5;
		this._world.hitArea.y = 0;
		this._world.hitArea.width = P3.View.width;
		this._world.hitArea.height = P3.View.height;

		if (this._dialog) {
			this._dialog?.position.set(common.STAGE_WIDTH * 0.5, common.STAGE_HEIGHT * 0.5);
		}

		if (this._alertDialog) {
			this._alertDialog?.position.set(common.STAGE_WIDTH * 0.5, common.STAGE_HEIGHT * 0.5 - this._alertDialog.height * 0.5);
		}

		if (this._fade) {
			this._fade.width = P3.View.width;
			this._fade.height = P3.View.height;
			this._fade.x = (common.STAGE_WIDTH - P3.View.width) * 0.5;
		}
	}

	update() {
		if (!this._canUpdate) {
			return;
		}

		this._gui.update();
		this._world.updateCamera(window.dt);

		if (common.paused) {
			return;
		}

		super.update();

		this._scenario.update(window.dt);

		if (this._inMenu) {
			return;
		}

		this._world.update(window.dt);

		this.updateTooltip();
		this.updateProbability();
	}

	show() {
		super.show();
		this._inMenu = false;
	}

	_onKeyUp(event) {
		if (P3.scene.top === this && event.key === 'Backspace' && common.popupOpen) {
			if (this._gui.dialog && this._gui.dialog.closeButton) {
				this._gui.dialog.closeButton.signals.click.dispatch();
			}

			this._gui.overview.onCloseButtonClick();
			this._gui.closeOpenDialog();
			this._gui.onDialogDestroy();
		}

		// Debug shortcuts (only active when ?debug=true)
		if (common.debug && P3.scene.top === this) {
			if (event.key === 'W') this._debugSkipToReport(true);
			if (event.key === 'X') this._debugSkipToReport(false);
		}
	}

	// Force-end the scenario and jump straight to the Report screen.
	// win=true → certificate button will be visible; win=false → loss report.
	_debugSkipToReport(win) {
		this._scenario._hasCompletedObjectives = win;
		try {
			this._scenario._approachSFX && this._scenario._approachSFX.stop();
		} catch {}
		this._scenario.ended = true;
		this._scenario.determineOutcome();
		// determineOutcome checks destroyed buildings/deaths etc — bypass all that for debug
		this._scenario._win = win;
		this._inMenu = true;
		this.signals.report.dispatch();
	}

	hide() {
		super.hide();

		this._inMenu = true;

		if (this._scenario.ended === true) {
			this._gui.disasterButton.visible = false;
			this._gui.completeButton.visible = true;
		}
	}

	initGUI() {
		this._gui = new HUD();
		this.addChild(this._gui);

		this._gui.ews.signals.evacClick.add(this.calculateSheltered, this);

		this._gui.disasterButton.signals.click.add(() => this.onDisasterButtonClick(false), this);
		this._gui.showRiskButton.signals.click.add(this.onShowRiskButtonClick, this);
		this._gui.settingsButton.signals.click.add(this.onSettingsClick, this);
		this._gui.achievements.signals.opened.add(this.onAchievementOpen, this);

		this._gui.achievementsButton.on('pointertap', this._onAchievementsButtonTap, this);
		this._gui.completeButton.on('pointertap', this._onCompleteButtonClick, this);
		this._gui.overviewButton.signals.click.add(this._onOverviewButtonClick, this);
		this._gui.achievements.closeButton.signals.click.add(this._onAchievementsCloseButtonClick, this);
		this._gui.overview.closeButton.signals.click.add(this._onOverviewCloseButtonClick, this);

		this._gui.ews.world = this._world;
		this._gui.ews.communityCenter = this._world.getStructureByName('community_center');
	}

	_onAchievementsButtonTap() {
		if (!this._gui.achievements.firstUnlocked) return;

		this._gui.closeOpenDialog();
		this._inMenu = true;
		common.paused = true;
		this._gui.achievements.appear();
		common.popupOpen = true;
		P3.audio.playSound('sfx_popup');
	}

	_onOverviewButtonClick() {
		this._gui.closeOpenDialog();
		this._inMenu = true;
		this._gui.overview.appear();
		common.popupOpen = true;
		P3.audio.playSound('sfx_popup');
	}

	_onOverviewCloseButtonClick() {
		this._inMenu = false;
		P3.audio.playSound('sfx_popup');
		common.popupOpen = false;
	}

	_onAchievementsCloseButtonClick() {
		this._inMenu = false;
		common.paused = false;
		P3.audio.playSound('sfx_popup');
		common.popupOpen = false;
	}

	_onCompleteButtonClick() {
		this._gui.closeOpenDialog();
		this.signals.next.dispatch(true);
		P3.audio.playSound('sfx_popup');
		common.popupOpen = false;
	}

	updateTooltip() {
		if (PIXI.utils.isMobile.any || this._world.camera._isMoving) {
			this._tooltip.visible = false;

			return;
		}

		const tile = this._world.currentTile;

		if (!tile || !P3.input.mouse) {
			return;
		}

		const position = this.toLocal(P3.input.mouse, common.stage);

		this._tooltip.position.set(Math.round(position.x), Math.round(position.y));

		if (this._selectTooltip.visible || this._gui.isOver) {
			this._tooltip.visible = false;
		} else {
			if (tile === this._world.previousTile) {
				return;
			}
			this._tooltip.visible = true;

			const t = tile.structure > 1 ? tile.data.structure : tile.data.ground;
			const desc = t && t.description ? i18next.t(t.description) : '...';
			const pop = t && t.population ? t.population : 0;
			const strength = tile.strength ? tile.strength : 0;
			const risk = tile.risk ? tile.risk : 0;

			this._tooltip.set(desc, pop, strength, risk);
			this._world.previousTile = this._world.currentTile;
		}
	}

	buildStructure(tile, id, cost) {
		if (this._scenario.budget.remaining < cost) {
			this.onObjectivesComplete('cash');

			return;
		}

		P3.audio.playSound('sfx_basket_purchase_complete_00');

		const structure = this._world.getStructure(id);

		if (structure.group) {
			const random = [];

			for (const i in this._world.structset) {
				if (Object.hasOwn(this._world.structset, i)) {
					const t = this._world.structset[i];

					t.group === structure.group && random.push(t.id);
				}
			}
			id = P3.utils.selectRandom(random);
		}

		common.lastBuild.special = structure.special;

		this._world.addStructure(id, tile);
		if (common.selectedLevel === 'tsunami' && tile.data.ground.id === 99) {
			tile.ground = 4;
			tile.isFloating = false;
		}
		tile.playBuildAnim();

		const oldRisk = tile.risk;

		tile.riskFactor = Math.min(2.5, tile.riskFactor + (Math.random() * 0.5));
		if (tile.riskFactor <= 0.8) {
			tile.risk = 4;
		} else if (tile.riskFactor > 0.8 && tile.riskFactor <= 1.2) {
			tile.risk = 3;
		} else if (tile.riskFactor > 1.2 && tile.riskFactor <= 1.4) {
			tile.risk = 2;
		} else if (tile.riskFactor > 1.4 && tile.riskFactor <= 2) {
			tile.risk = 1;
		} else if (tile.riskFactor > 2) {
			tile.risk = 0;
		}

		if (structure.sound) {
			P3.audio.playSound(structure.sound);
		}

		if (structure.type === 1) {
			this._scenario.budget.housing += cost;
			common.score += 170;
			this._gui.updateScore();
		} else {
			this._scenario.budget.defences += cost;
			common.score += 450;
			this._gui.updateScore();
		}
		if (this._scenario.population.unhoused < structure.population) {
			this._scenario.population.housed += this._scenario.population.unhoused;
			this._scenario.population.unhoused = 0;
		} else {
			this._scenario.population.unhoused -= structure.population;
			this._scenario.population.housed += structure.population;
		}

		this._scenario.budget.remaining -= cost;
		this._gui.updateBudget();
		this._gui.updatePopulation();
		this._scenario.onBuild(structure.special);

		if (tile.risk === 0) {
			common.achievements.unlockAchievement(1);
		}
		if (common.selectedLevel === 'tsunami') {
			if (id === 385) {
				common.achievements.unlockAchievement(2);
			}
			if (id === 56) {
				common.achievements.unlockAchievement(5);
			}
			if (id === 300) {
				common.achievements.unlockAchievement(10);
			}
			if (id === 230 || id === 231) {
				common.achievements.unlockAchievement(14);
			}
		} else if (common.selectedLevel === 'earthquake') {
			if (structure.type === 1) {
				common.achievements.unlockAchievement(5);
			}
			if (id === 221) {
				common.achievements.unlockAchievement(10);
			}
		} else if (common.selectedLevel === 'flood') {
			if (structure.type === 1) {
				common.achievements.unlockAchievement(5);
			}
			if (structure.type === 0) {
				common.achievements.unlockAchievement(10);
			}

			if (oldRisk === 4 && structure.type === 0) {
				common.achievements.unlockAchievement(0);
			}
			if (oldRisk === 4 && structure.type === 1) {
				common.achievements.unlockAchievement(14);
			}
			if ([305, 306, 307, 303].includes(id)) {
				common.achievements.unlockAchievement(2);
			}
		} else if (common.selectedLevel === 'wildfire') {
			if ([21, 22, 23].includes(id)) {
				common.achievements.unlockAchievement(4);
			}
			if ([200, 202, 220].includes(structure.group)) {
				common.achievements.unlockAchievement(14);
			}
		} else if (common.selectedLevel === 'hurricane') {
			if (id === 70) {
				common.achievements.unlockAchievement(2);
			}
			if (id === 69) {
				common.achievements.unlockAchievement(5);
			}
			if (id === 16) {
				common.achievements.unlockAchievement(9);
			}
			if (tile.data.structure.group === 40) {
				common.achievements.unlockAchievement(10);
			}
		}

		if (structure.special === 'hospital') {
			common.achievements.unlockAchievement(9);
		} else if (structure.special === 'school') {
			common.achievements.unlockAchievement(13, 'earthquake');
			common.achievements.unlockAchievement(10, 'wildfire');
			common.achievements.unlockAchievement(13, 'tsunami');
		}

		if (this._scenario.budget.remaining < this._world.cheapestItem) {
			this.onObjectivesComplete('cash');
		}
	}

	upgradeBuilding(tile, id, cost) {
		if (this._scenario.budget.remaining < cost) {
			return;
		}

		P3.audio.playSound('sfx_basket_purchase_complete_00');

		for (const u of id) {
			tile.upgrades = u;

			if (u === 7 || u === 13) {
				common.achievements.unlockAchievement(7, 'tsunami');
			}
			if (u === 9 || u === 10) {
				common.achievements.unlockAchievement(3);
			}
			if (u === 11) {
				common.achievements.unlockAchievement(12, 'tsunami');
			}
			if (u === 12) {
				common.achievements.unlockAchievement(11);
			}
			if (u === 8) {
				common.achievements.unlockAchievement(2, 'earthquake');
			}
			if (u === 15) {
				common.achievements.unlockAchievement(4, 'earthquake');
			}
			if (u === 14) {
				common.achievements.unlockAchievement(7, 'earthquake');
			}
			if (u === 13) {
				common.achievements.unlockAchievement(8, 'earthquake');
			}
			if (u === 5 || u === 4) {
				common.achievements.unlockAchievement(12, 'earthquake');
			}
			if (u === 3) {
				common.achievements.unlockAchievement(14, 'earthquake');
			}
			if (u === 13) {
				common.achievements.unlockAchievement(12, 'flood');
			}
			if (u === 14 && tile.data.structure.type === 1) {
				common.achievements.unlockAchievement(13, 'flood');
			}
			if (u === 22) {
				common.achievements.unlockAchievement(0, 'wildfire');
			}
			if (u === 6 || u === 14) {
				common.achievements.unlockAchievement(7, 'wildfire');
			}
			if (u === 16 && common.selectedLevel === 'flood') {
				common.achievements.unlockAchievement(4);
				this._world.removeStructure(tile);
				this._world.addStructure(222, tile);
			}
			if (u === 19 && tile.data.structure.group === 24) {
				common.achievements.unlockAchievement(0, 'hurricane');
			}

			if (u === 25 && [21, 18].includes(tile.data.structure.group)) {
				common.achievements.unlockAchievement(13, 'hurricane');
			}
			if ([2, 5, 7, 8, 11].includes(tile.data.structure.group) && u === 20) {
				common.achievements.unlockAchievement(4, 'hurricane');
			}
			if ([2, 5, 7, 8, 11].includes(tile.data.structure.group) && u === 22) {
				common.achievements.unlockAchievement(14, 'hurricane');
			}
			if ([2, 5, 7, 8, 11].includes(tile.data.structure.group)) {
				common.achievements.unlockAchievement(7, 'hurricane');
			}
			if (u === 9 || u === 11) {
				common.achievements.unlockAchievement(12, 'hurricane');
			}

			if (u === 13 && common.selectedLevel === 'tsunami') {
				const upgrades = tile.upgrades.slice(0);

				const newStructure = tile.structure + 1000;

				this._world.removeStructure(tile);
				this._world.addStructure(newStructure, tile);

				tile.upgrades.push(...upgrades);
			}

			if (common.selectedLevel === 'wildfire' && u === 20) {
				this._scenario.onUpgrade();
			} else if (common.selectedLevel === 'earthquake' && u === 15) {
				this._scenario.onUpgrade();
			} else if (common.selectedLevel === 'flood' && u === 16) {
				this._scenario.onUpgrade();
			} else if (common.selectedLevel === 'hurricane' && u === 19 && tile.data.structure.group === 24) {
				this._scenario.onUpgrade();
			}

			if (![9, 10, 11, 12].includes(u)) {
				continue;
			}
			if (common.selectedLevel === 'tsunami' && tile.data.structure.id === 236) {
				common.scenario.ccUpgrades.push(u);
			}
			if (common.selectedLevel === 'earthquake' && tile.data.structure.id === 230) {
				common.scenario.ccUpgrades.push(u);
			}
			if (common.selectedLevel === 'flood' && tile.data.structure.id === 220) {
				common.scenario.ccUpgrades.push(u);
			}
			if (common.selectedLevel === 'wildfire' && tile.data.structure.id === 240) {
				common.scenario.ccUpgrades.push(u);
			}
			if (common.selectedLevel === 'hurricane' && tile.data.structure.id === 240) {
				common.scenario.ccUpgrades.push(u);
			}
		}

		if (tile.data.structure.type === 1) {
			common.achievements.unlockAchievement(7, 'flood');
		}

		this._scenario.budget.housing += cost;
		this._scenario.budget.remaining -= cost;
		this._gui.updateBudget();
		this._gui.updatePopulation();
		tile.setStrength();

		tile.riskFactor = Math.min(2.5, tile.riskFactor + (Math.random() * 0.5));
		if (tile.riskFactor <= 0.8) {
			tile.risk = 4;
		} else if (tile.riskFactor > 0.8 && tile.riskFactor <= 1.2) {
			tile.risk = 3;
		} else if (tile.riskFactor > 1.2 && tile.riskFactor <= 1.4) {
			tile.risk = 2;
		} else if (tile.riskFactor > 1.4 && tile.riskFactor <= 2) {
			tile.risk = 1;
		} else if (tile.riskFactor > 2) {
			tile.risk = 0;
		}

		common.score += 1000;
		this._gui.updateScore();
		if (this._scenario.budget.remaining < this._world.cheapestItem) {
			this.onObjectivesComplete('cash');
		}
	}

	updateProbability() {
		if (!this._scenario.finished) {
			this._gui.probability = P3.math.clamp((this._scenario.elapsedTime / this._scenario.time) * 100, 0, 100);
		} else {
			this._gui.probability = 100;
		}
	}

	_repeatDialog(cb) {
		if (this._dialog) {
			this._dialog.destroy();
			this._dialog = null;
		}

		this._fade = new PIXI.Graphics();
		this._fade.beginFill(0x0, 0.4);
		this._fade.drawRect(0, 0, P3.View.width, P3.View.height);
		this._fade.endFill();
		this._fade.position.set((common.STAGE_WIDTH - P3.View.width) * 0.5, 0);
		this._fade.interactive = true;
		this.addChild(this._fade);

		this._dialog = new ConfirmDialog(500, 600);
		this._dialog.setTitleText('gui.confirmdialog.title');
		this._dialog.setBodyText('gui.confirmdialog.body');
		this._dialog.position.set(common.STAGE_WIDTH * 0.5, common.STAGE_HEIGHT * 0.5);
		this._dialog.scale.set(0.8);
		new TWEEN.Tween(this._dialog.scale, this._tweens)
			.to({ x: 1, y: 1 }, 300)
			.easing(TWEEN.Easing.Back.Out)
			.start();

		const onClose = () => {
			if (this._fade) {
				this._fade.destroy();
				this._fade = null;
			}

			this._inMenu = false;
			this._dialog.destroy();
			this._dialog = null;
			common.popupOpen = false;
		};

		this._dialog.closeButton.signals.click.once(onClose, this);
		this._dialog.cancelButton.signals.click.once(onClose, this);

		this._dialog.confirmButton.signals.click.once(() => {
			if (this._fade) {
				this._fade.destroy();
				this._fade = null;
			}

			cb.call();
			this._dialog.destroy();
			this._dialog = null;
			common.popupOpen = false;
		});

		P3.audio.playSound('sfx_popup');
		this.addChild(this._dialog);
		common.popupOpen = true;
	}

	onWorldTileSelect(world, tile) {
		if (this._dialog) {
			this._dialog.destroy();
			this._dialog = null;
		}

		this._gui.closeOpenDialog();
		common.popupOpen = false;

		if (tile.disabled) {
			return;
		}

		P3.audio.playSound('sfx_genericclick');

		this._selectedTile = tile;
		if (this._selectTooltip && tile && P3.input.mouse && !this._world.isCameraPan) {
			const position = tile.down.position; // we use the down tiles position because for whatever reason, tile.position gives one tile up.

			this._selectTooltip.position.set(Math.round(position.x), Math.round(position.y));
			this._selectTooltip.visible = true;

			if (this._scenario.ended || this._scenario.finished) {
				this._selectTooltip.setVisible(tile, [2]);
			} else if (tile.data.structure && tile.data.structure.type === -2) {
				this._selectTooltip.setVisible(tile, [2]);
			} else if (tile.data.structure && (((common.selectedLevel === 'wildfire' || common.selectedLevel === 'earthquake') && (tile.data.structure.group === 2 || tile.data.structure.group === 16)))) {
				this._selectTooltip.setVisible(tile, [1, 2, 3]);
			} else if (common.selectedLevel === 'flood' && tile.data.structure && tile.data.structure.id === 222) {
				this._selectTooltip.setVisible(tile, [2]);
			} else if (tile.data.structure && (tile.data.structure.type === 1 || tile.data.structure.type === -1)) {
				this._selectTooltip.setVisible(tile, [1, 2, 4]);
			} else if (tile.isWater) {
				if (['flood', 'earthquake', 'wildfire'].includes(common.selectedLevel)) {
					this._selectTooltip.setVisible(tile, [2]);
				} else if (tile.data.structure && tile.data.structure.type === 0) {
					this._selectTooltip.setVisible(tile, [1, 2]);
				} else {
					this._selectTooltip.setVisible(tile, [0, 2]);
				}
			} else if (common.lastBuild.id <= 0) {
				this._selectTooltip.setVisible(tile, [0, 2, 3]);
			} else {
				this._selectTooltip.setVisible(tile, [0, 2, 3, 5]);
			}
			this._selectTooltip.alpha = 0.6;
			new TWEEN.Tween(this._selectTooltip, this._tweens)
				.to({
					alpha: 1,
				}, 500)
				.easing(TWEEN.Easing.Back.Out)
				.start();
		}
	}

	onDefendClick() {
		this._gui.closeOpenDialog();

		this.signals.develop.dispatch(this, this._selectedTile, 0);
		P3.audio.playSound('sfx_popup');
		this._selectTooltip.visible = false;
		this._selectTooltip.setInvisible();
	}

	onInfoClick() {
		this._gui.closeOpenDialog();

		this._gui.showTileInfoDialog(this._selectedTile);
		this._selectTooltip.visible = false;
		this._selectTooltip.setInvisible();

		if (!this._selectedTile.data.structure) {
			return;
		}

		const id = this._selectedTile.data.structure.id;
		const group = this._selectedTile.data.structure.group;

		if (common.selectedLevel === 'tsunami') {
			if (id === 157) {
				common.achievements.unlockAchievement(4);
			}
			if (id === 300) {
				common.achievements.unlockAchievement(10);
			}
			if (id === 236) {
				common.achievements.unlockAchievement(8);
			}
		} else if (common.selectedLevel === 'earthquake') {
			if ([204, 207, 209].includes(group)) {
				common.achievements.unlockAchievement(0);
			}
		} else if (common.selectedLevel === 'flood') {
			if (id === 220) {
				common.achievements.unlockAchievement(8);
			}
		} else if (common.selectedLevel === 'wildfire') {
			if ([200, 202, 220].includes(group)) {
				common.achievements.unlockAchievement(5);
			}
			if (id === 240) {
				common.achievements.unlockAchievement(8);
			}
		} else if (common.selectedLevel === 'hurricane') {
			if (id === 240) {
				common.achievements.unlockAchievement(8);
			}
			if (group === 40) {
				common.achievements.unlockAchievement(10);
			}
		}
	}

	onDevelopClick() {
		this._gui.closeOpenDialog();

		!this._selectedTile.isWater && this.signals.develop.dispatch(this, this._selectedTile, 1);
		P3.audio.playSound('sfx_popup');
		this._selectTooltip.visible = false;
		this._selectTooltip.setInvisible();
	}

	onDemolishClick() {
		this._gui.closeOpenDialog();

		if (!this._selectedTile.data.structure) {
			return;
		}
		if (this._selectedTile.data.structure.type === -1) {
			P3.audio.playSound('sfx_incorrect');

			return;
		} // prevent Community Center from being demolished.

		const cost = this._selectedTile.data.structure.devcost;

		if (this._scenario.budget.remaining < cost) {
			this.onObjectivesComplete('cash');

			return;
		}

		this._scenario.budget.remaining -= cost;
		this._gui.updateBudget();

		this._scenario.population.unhoused += this._selectedTile.data.structure.population;
		this._scenario.population.housed -= this._selectedTile.data.structure.population;
		this._gui.updatePopulation();

		this._selectedTile.playDestroyAnim();
		this._selectedTile.calculateRiskCategory(this._selectedTile.level);
		this._selectTooltip.visible = false;
		this._selectTooltip.setInvisible();
		this._scenario.onDestroy(this._selectedTile.data.structure.special);

		if (common.selectedLevel === 'wildfire' && this._selectedTile.data.structure.group === 2) {
			this._world.createFirebreak(this._selectedTile);
			common.achievements.unlockAchievement(2);
			common.achievements.unlockAchievement(13);

			if (!common.achievements.trackers.firebreak) {
				common.achievements.setTracker('firebreak', 1);
			} else {
				common.achievements.trackers.firebreak++;
				if (common.achievements.trackers.firebreak >= 6) {
					common.achievements.unlockAchievement(12);
				}
			}
		} else {
			this._world.removeStructure(this._selectedTile);
		}

		const sounds = [
			'sfx_collapse1',
			'sfx_collapse2',
			'sfx_collapse3',
			'sfx_collapse4',
		];

		P3.audio.playSound(P3.utils.selectRandom(sounds));

		if (this._scenario.budget.remaining < this._world.cheapestItem) {
			this.onObjectivesComplete('cash');
		}
	}

	onSettingsClick() {
		common.paused = true;
		this.signals.pause.dispatch();
	}

	destroy() {
		if (PIXI.utils.isMobile.any && screenfull.isEnabled) {
			screenfull.off('change', this.onScreenfullChange.bind(this));
		}

		P3.input.signals.keyUp.detach(this._keyUpSignal);
		this._canUpdate = false;
		this._gui.destroy();
		this._world.destroy();
		this._scenario.destroy();

		common.started = false;
		common.paused = false;
		common.popupOpen = false;

		super.destroy();
	}

	onScreenfullChange() {
		this.gui.resize();
	}

	onUpgradeClick() {
		this._gui.closeOpenDialog();

		this.signals.develop.dispatch(this, this._selectedTile, 3);
		this._selectTooltip.visible = false;
		this._selectTooltip.setInvisible();
	}

	onRepeatClick() {
		this._gui.closeOpenDialog();

		if (!common.lastBuild || (common.lastBuild.special === 'school' && this._scenario.targetProgress.school >= this._scenario.targets.school)
			|| (common.lastBuild.special === 'hospital' && this._scenario.targetProgress.hospital >= this._scenario.targets.hospital)) {
			return;
		}

		if (this._selectedTile.data.ground.devcost !== common.lastBuild.devcost) {
			this._repeatDialog(() => {
				this.buildStructure(this._selectedTile, common.lastBuild.id, common.lastBuild.cost + this._selectedTile.data.ground.devcost);
				this._selectTooltip.visible = false;
				this._selectTooltip.setInvisible();
				common.lastBuild.cost = (common.lastBuild.cost - common.lastBuild.devcost) + this._selectedTile.data.ground.devcost;
				common.lastBuild.devcost = this._selectedTile.data.ground.devcost;
			}, (common.lastBuild.cost - common.lastBuild.devcost) + this._selectedTile.data.ground.devcost);

			return;
		}
		this.buildStructure(this._selectedTile, common.lastBuild.id, common.lastBuild.cost);
		this._selectTooltip.visible = false;
		this._selectTooltip.setInvisible();
	}

	onCameraPan() {
		if (this._selectTooltip) {
			this._selectTooltip.visible = false;
			this._selectTooltip.setInvisible();
		}
	}

	onDisasterButtonClick(force = false) {
		if (!common.debug && (!this._scenario.disaster.canStart || this._gui.ews._extended)) {
			if (this._gui.ews._extended) {
				return;
			}

			this._gui.closeOpenDialog();

			this._fade = new PIXI.Graphics();
			this._fade.beginFill(0x0, 0.4);
			this._fade.drawRect(0, 0, P3.View.width, P3.View.height);
			this._fade.endFill();
			this._fade.position.set((common.STAGE_WIDTH - P3.View.width) * 0.5, 0);
			this._fade.interactive = true;
			this.addChild(this._fade);

			this._dialog = new WarningDialog(`icon_${common.selectedLevel}`);

			this._dialog.toggleCancelButton();
			this._dialog.body = i18next.t('gui.dialog.disasterNotReady');
			this._dialog.confirmLabel.text = i18next.t('gui.general.options.ok');
			this._dialog.position.set(common.STAGE_WIDTH * 0.5, common.STAGE_HEIGHT * 0.5);
			this.addChild(this._dialog);

			this._dialog.signals.confirm.add(() => {
				if (this._fade) {
					this._fade.destroy();
					this._fade = null;
				}
				this._dialog = null;
			});

			return;
		}

		const onConfirm = () => {
			if (this._selectTooltip) {
				this._selectTooltip.visible = false;
				this._selectTooltip.setInvisible();
			}

			if (this._fade) {
				this._fade.destroy();
				this._fade = null;
			}

			const filter = new PIXI.filters.ColorMatrixFilter();

			filter.desaturate();
			this._gui.disasterButton.filters = [filter];

			this._scenario.disaster.canStart = true;
			this._gui.probability = 100;
			this._gui.picture.playAnimation('sad');
			this._scenario.finished = true;
			this._scenario.ccUpgrades.length > 0 ? this._gui.ews.appear() : common.scenario.disaster.start();
			this._dialog = null;
		};

		if (!force) {
			this._gui.closeOpenDialog();

			this._fade = new PIXI.Graphics();
			this._fade.beginFill(0x0, 0.4);
			this._fade.drawRect(0, 0, P3.View.width, P3.View.height);
			this._fade.endFill();
			this._fade.position.set((common.STAGE_WIDTH - P3.View.width) * 0.5, 0);
			this._fade.interactive = true;
			this.addChild(this._fade);

			this._dialog = new WarningDialog(`icon_${common.selectedLevel}`);

			this._dialog.body = i18next.t('gui.dialog.disasterConfirm');
			this._dialog.position.set(common.STAGE_WIDTH * 0.5, common.STAGE_HEIGHT * 0.5);
			this.addChild(this._dialog);

			this._dialog.signals.confirm.add(onConfirm);
			this._dialog.signals.deny.add(() => {
				if (this._fade) {
					this._fade.destroy();
					this._fade = null;
				}
				this._dialog = null;
			});
		} else {
			onConfirm();
		}
	}

	calculateSheltered() {
		const numUpgrades = this._scenario.ccUpgrades.length / 4;
		const unhoused = this._scenario.population.unhoused;
		const sheltered = Math.floor(unhoused * numUpgrades);

		if (unhoused <= 0) {
			this._scenario.population.unhoused = 0;
			this._scenario.population.sheltered = P3.math.randomInt(this._scenario.population.housed * 0.1, this._scenario.population.housed * 0.75);
		} else {
			this._scenario.population.unhoused = unhoused - sheltered;
			this._scenario.population.sheltered = sheltered;
		}

		this._gui.updatePopulation();

		const peopleStructures = this._world.peopleStructures;

		const length = Math.round(peopleStructures.length / this._scenario.ccUpgrades.length);

		for (let i = 0; i < length; i++) {
			new TWEEN.Tween({}, this.tweens)
				.to({}, i * (20000 / length))
				.easing(TWEEN.Easing.Linear.None)
				.onComplete(() => {
					this._world.removeStructure(peopleStructures[i]);
				})
				.start();
		}
	}

	onDisasterStart() {
		if (this._inMenu) {
			this._gui.achievements.visible = false;
			this._gui.overview.visible = false;
			common.popupOpen = false;
			this._inMenu = false;
		}
	}

	onDisasterEnd() {
		this._scenario._approachSFX.stop();
		this._scenario.ended = true;
		this._scenario.determineOutcome();

		P3.utils.delay(2, () => {
			this._inMenu = true;
			this.signals.next.dispatch(this);
		}, this);
	}

	onShowRiskButtonClick() {
		this._world.toggleRisk();
		common.achievements.unlockAchievement(6);
	}

	onAchievementUnlock() {
		this._gui.achievements.checkAchievements();
		const unopened = this._gui.achievements.new.length;

		this._gui.achievementsButton.notificationIcon.label.text = unopened;
		this._gui.achievementsButton.notificationIcon.visible = unopened > 0;

		this._gui.picture.playAnimation('happy');
		this._gui.updateScore();
	}

	onAchievementOpen() {
		const unopened = this._gui.achievements.new.length;

		this._gui.achievementsButton.notificationIcon.label.text = unopened;
		this._gui.achievementsButton.notificationIcon.visible = unopened > 0;
	}

	onObjectivesComplete(cause) {
		if (this.startNowMessage) {
			return;
		}
		this._gui.closeOpenDialog();

		this._fade = new PIXI.Graphics();
		this._fade.beginFill(0x0, 0.4);
		this._fade.drawRect(0, 0, P3.View.width, P3.View.height);
		this._fade.endFill();
		this._fade.position.set((common.STAGE_WIDTH - P3.View.width) * 0.5, 0);
		this._fade.interactive = true;
		this.addChild(this._fade);

		this._gui.disasterButton.filters = null;
		this._gui.disasterButton.animate = true;
		this._gui.disasterButton.animateIcon = true;

		const text = cause === 'objectives' ? i18next.t('gui.game.endtext.housed') : i18next.t('gui.game.endtext.cash');

		const dialog = new AlertDialog(i18next.t('gui.general.advisor'), text, 'happy');

		dialog.position.set(common.STAGE_WIDTH * 0.5, common.STAGE_HEIGHT * 0.5 - dialog.height * 0.5);
		this.addChild(dialog);

		dialog.body.okButton.destroy();

		dialog.body.okButton = common.buttonFactory.createButton({
			normal: { textureName: 'btn_blue_fill_large_9slice_out', width: 220, height: 75, left: 35, top: 35, right: 35, bottom: 35 },
			over: { textureName: 'btn_blue_fill_large_9slice_over', width: 220, height: 75, left: 35, top: 35, right: 35, bottom: 35 },
		});
		dialog.body.okButton.position.set(dialog.body.width * 0.5 + dialog.body.okButton.width * 0.5 + 10, dialog.body.text.y + dialog.body.text.height + 75);
		dialog.body.addChild(dialog.body.okButton);

		dialog.body.okButton.label = new Text('gui.general.options.yes', { font: '30px', fontWeight: 'bold', align: 'left', tint: 0xFFFFFF });
		dialog.body.okButton.label.anchor.set(0.5);
		dialog.body.okButton.animation.addChild(dialog.body.okButton.label);

		dialog.body.okButton.accessibleTitle = dialog.body.okButton.label.text;

		dialog.body.noButton = common.buttonFactory.createButton({
			normal: { textureName: 'btn_black_outline_9slice_out', width: 220, height: 75, left: 35, top: 35, right: 35, bottom: 35 },
			over: { textureName: 'btn_black_outline_9slice_over', width: 220, height: 75, left: 35, top: 35, right: 35, bottom: 35 },
		});
		dialog.body.noButton.position.set(dialog.body.width * 0.5 - dialog.body.noButton.width * 0.5 - 10, dialog.body.text.y + dialog.body.text.height + 75);
		dialog.body.addChild(dialog.body.noButton);

		dialog.body.noButton.label = new Text('gui.general.options.no', { font: '30px', fontWeight: 'bold', align: 'left', tint: 0x0 });
		dialog.body.noButton.label.anchor.set(0.5);
		dialog.body.noButton.animation.addChild(dialog.body.noButton.label);

		dialog.body.noButton.accessibleTitle = dialog.body.noButton.label.text;

		const onCloseClick = () => {
			dialog.destroy();
			if (this._fade) {
				this._fade.destroy();
				this._fade = null;
			}
			common.scenario.disaster.canStart = true;
			this.startNowMessage = true;
			this._alertDialog = null;
		};

		dialog.body.noButton.signals.click.once(onCloseClick, this);
		dialog.closeButton.signals.click.once(onCloseClick, this);

		dialog.body.okButton.signals.click.once(() => {
			dialog.destroy();
			if (this._fade) {
				this._fade.destroy();
				this._fade = null;
			}

			this._scenario.disaster.canStart = true;
			this._gui.probability = 100;
			this._gui.picture.playAnimation('sad');
			this._scenario.finished = true;
			this._scenario.ccUpgrades.length > 0 ? this._gui.ews.appear() : common.scenario.disaster.start();
			this.startNowMessage = true;
			this._alertDialog = null;
		});

		dialog.resize(dialog.body.okButton.y + dialog.body.okButton.height + 25);

		dialog.scale.set(0.8);
		new TWEEN.Tween(dialog.scale, this._tweens)
			.to({
				x: 1,
				y: 1,
			}, 300)
			.easing(TWEEN.Easing.Back.Out)
			.start();

		P3.audio.playSound('sfx_popup');

		this._alertDialog = dialog;
	}
}
